// Unlocks (IMPLEMENTATION §0.4, M4a). `hasEffect` gates features (counter selling, Quick Merge, Merge All, the
// shop, Build mode, expansions, …) on the unlocked ids; `applyUnlock` records an unlock, applies its
// state-changing effects and moves the goal chip; `fireUnlocks` is the phase-7 trigger engine.
//   Engine: unlocks are tried in table order; one fires when it is version-active, not yet unlocked, its
//   compiled prerequisite `after` has fired ("masks nest": the nearest earlier unlock with chips — toast-only
//   unlocks such as u07 / u19 are side branches that block nothing) and its trigger holds. One pass per tick
//   cascades (an unlock can enable the next in the same tick). `start` unlocks (u00) belong to the start
//   state (setup.coldOpenState) and are never fired by the engine, so a board built without them (tests,
//   `unlocks: none` scenarios) stays inert.
//   Goal: the latest fired unlock with chips (index into defs.unlocks); a `goal` event marks the change.
import { addBuilding, makeDeposit } from './buildings.ts';
import type { Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { isActive, type SimDefs, type UnlockDef } from './defs.ts';
import type { EffectId, UnlockId } from './gen/ids.ts';
import { nearestFree } from './occupancy.ts';
import { triggerHolds } from './rules/triggers.ts';
import type { SimState } from './state.ts';

export function isUnlocked(s: SimState, id: UnlockId): boolean {
  return s.progress.unlocked.includes(id);
}

/** True when an unlocked, version-active unlock grants `eff`. */
export function hasEffect(s: SimState, d: SimDefs, eff: EffectId): boolean {
  for (const u of d.unlocks) {
    if (!isUnlocked(s, u.id)) continue;
    for (const e of u.effects) if (e.id === eff && isActive(d, e.version)) return true;
  }
  return false;
}

/** Index of an unlock in defs.unlocks (-1 = unknown). */
export function unlockIndex(d: SimDefs, id: UnlockId): Int {
  for (let i = 0; i < d.unlocks.length; i++) if (d.unlocks[i]?.id === id) return i;
  return -1;
}

function applyEffect(c: Ctx, eff: EffectId): void {
  switch (eff) {
    case 'gallery.practice': {
      const p = c.d.buildings.gallery.preplaced;
      for (const b of c.s.buildings) {
        if (b.type === 'gallery' && b.x === p.x && b.y === p.y) {
          b.practice = c.d.economy.coldOpen.practiceRounds;
          b.manual = true;
        }
      }
      return;
    }
    case 'deposit.lead.rise': {
      // Pre-placed deposits rise at their cell (the nearest free cell if something sits there).
      for (const dep of c.d.deposits) {
        if (dep.preplaced === null || dep.preplaced.effect !== eff) continue;
        const at = nearestFree(c.occ, dep.preplaced.x, dep.preplaced.y);
        if (at === null) continue;
        const b = makeDeposit(c.s, dep.id, at.x, at.y, 1);
        addBuilding(c, b);
        c.ev.push({
          e: 'place',
          bid: b.id,
          type: 'deposit',
          x: b.x,
          y: b.y,
          dir: b.dir,
          ghost: false,
        });
      }
      return;
    }
    default:
      // Flag effects: read through hasEffect.
      return;
  }
}

/** Moves the goal chip to unlock `i` when it has chips and is newer than the current goal. */
export function noteGoal(c: Ctx, i: Int): void {
  const u = c.d.unlocks[i];
  if (u === undefined || u.chips.length === 0 || i <= c.s.progress.goal) return;
  c.s.progress.goal = i;
  c.ev.push({ e: 'goal', goal: i });
}

/** Records `id` (once) and applies its version-active effects. False when already unlocked/inactive. */
export function applyUnlock(c: Ctx, id: UnlockId): boolean {
  if (isUnlocked(c.s, id)) return false;
  const i = unlockIndex(c.d, id);
  const u = c.d.unlocks[i];
  if (u === undefined || !isActive(c.d, u.version)) return false;
  c.s.progress.unlocked.push(id);
  c.ev.push({ e: 'unlock', id });
  for (const e of u.effects) if (isActive(c.d, e.version)) applyEffect(c, e.id);
  noteGoal(c, i);
  return true;
}

/** Every version-active unlock, in table order (debug "unlock all"). Returns how many were new. */
export function applyAllUnlocks(c: Ctx): Int {
  return applyUnlocksUpTo(c, c.d.unlocks.length - 1);
}

/** The cold-open tin after the empty click (what u01's `practiceEmpty` trigger reads). */
export function emptyPracticeTin(s: SimState): void {
  for (const b of s.buildings) {
    if (b.type !== 'gallery') continue;
    b.practice = 0;
    b.manual = false;
  }
}

/**
 * Every version-active unlock up to table index `last`, in order (the debug goal jump). `asPlayed`: put the
 * board into the state each trigger reads first — a `practiceEmpty` unlock (u01) empties the practice tin
 * before it applies, like a mid-progression start (setup.coldOpenState `unlocked`). Returns how many were new.
 */
export function applyUnlocksUpTo(c: Ctx, last: Int, asPlayed = false): Int {
  let n = 0;
  for (let i = 0; i <= last && i < c.d.unlocks.length; i++) {
    const u = c.d.unlocks[i] as UnlockDef;
    if (asPlayed && u.trigger.type === 'practiceEmpty' && !isUnlocked(c.s, u.id)) {
      emptyPracticeTin(c.s);
    }
    if (applyUnlock(c, u.id)) n++;
  }
  return n;
}

/** The engine's firing rule for one unlock, without the trigger (see the header). */
export function isPending(s: SimState, d: SimDefs, i: Int): boolean {
  const u = d.unlocks[i];
  if (u === undefined || !isActive(d, u.version) || isUnlocked(s, u.id)) return false;
  if (u.trigger.type === 'start') return false;
  return u.after === null || isUnlocked(s, u.after);
}

/** Phase 7 trigger engine: fires every pending unlock whose trigger holds, in table order. */
export function fireUnlocks(c: Ctx): Int {
  let n = 0;
  for (let i = 0; i < c.d.unlocks.length; i++) {
    if (!isPending(c.s, c.d, i)) continue;
    const u = c.d.unlocks[i] as UnlockDef;
    if (!triggerHolds(c.s, c.d, u.trigger)) continue;
    if (applyUnlock(c, u.id)) n++;
  }
  return n;
}
